import { createGlobalStyle } from 'styled-components';

// Global styles on the CSG palette (spec 2026-09-24 §8). Colours resolve
// through `theme.colors` → the --odin-* variables ClaudeVars sets on <html>.
// There are deliberately NO `.ant-*` rules here: antd takes its look from the
// root ConfigProvider (claudeAntdTheme); the few rules no token can express
// live in AntdOverrides.js, the only file outside antdTheme.js — and the only
// one anywhere outside src/theme/ is forbidden — that may name `.ant-*`.
export const GlobalStyles = createGlobalStyle`
  /* Gabarito (the brand face) is loaded from Google Fonts via index.html. Cascadia
     Mono — the brand's one permitted non-Gabarito face, for code and command text —
     is self-hosted (public/fonts, SIL OFL 1.1). */
  @font-face {
    font-family: 'Cascadia Mono';
    src: url(/fonts/CascadiaMono-Regular.woff2) format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    /* iOS Safari sizes the layout viewport to the widest element regardless of
       body overflow-x, which loads pages "zoomed in". clip (not hidden) so html
       doesn't become a scroll container and position:sticky keeps working. */
    overflow-x: clip;
  }

  body {
    font-family: ${({ theme }) => theme?.typography?.fontFamily?.primary || "'Gabarito', 'Avenir Next', Avenir, system-ui, 'Segoe UI', Roboto, sans-serif"};
    background: ${({ theme }) => theme?.colors?.primary || 'var(--odin-bg)'};
    color: ${({ theme }) => theme?.colors?.text || 'var(--odin-text-primary)'};
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    transition: background 0.3s ease, color 0.3s ease;
  }

  /* Display headings: Gabarito Bold in the Oxford Blue ink — the brand's heading
     rule (Bold, sentence case). h4+ stay Gabarito 600. */
  h1, h2, h3 {
    font-family: ${({ theme }) => theme.typography.fontFamily.display};
    color: ${({ theme }) => theme.colors.ink};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    letter-spacing: -0.015em;
    line-height: 1.2;
    transition: color 0.3s ease;
  }

  h4, h5, h6 {
    font-family: ${({ theme }) => theme.typography.fontFamily.heading};
    color: ${({ theme }) => theme.colors.text};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    line-height: ${({ theme }) => theme.typography.lineHeight.tight};
    transition: color 0.3s ease;
  }

  h1 {
    font-size: 34px;
    letter-spacing: -0.02em;
    line-height: 1.12;
  }

  h2 {
    font-size: 24px;
  }

  h3 {
    font-size: 19px;
  }

  h4 {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }

  /* Code styling */
  code {
    font-family: ${({ theme }) => theme.typography.fontFamily.secondary};
    background: var(--odin-code-bg);
    padding: 0.2em 0.4em;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    color: ${({ theme }) => theme.colors.textAccent};
  }

  /* Selection styling */
  ::selection {
    background: color-mix(in srgb, var(--odin-highlight) 30%, transparent);
    color: inherit;
  }

  /* Scrollbar styling */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.secondary};
  }

  ::-webkit-scrollbar-thumb {
    background: var(--odin-border-strong);
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.textMuted};
  }

  /* Focus ring — the Celestial Blue HSL triplet at 40% as a box-shadow */
  *:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px hsl(var(--odin-accent-focus) / 0.4);
  }

  /* Button reset */
  button {
    font-family: inherit;
    cursor: pointer;
    border: none;
    background: none;
  }

  /* Link reset */
  a {
    color: inherit;
    text-decoration: none;
  }

  /* Input reset */
  input, textarea, select {
    font-family: inherit;
    font-size: inherit;
    border: none;
    outline: none;
  }

  /* Keyframe animations */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }

  /* Responsive design */
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    body {
      font-size: ${({ theme }) => theme.typography.fontSize.sm};
    }

    h1 {
      font-size: 28px;
    }

    h2 {
      font-size: 22px;
    }

    h3 {
      font-size: 18px;
    }
  }
`;
